import { List } from 'immutable';
import * as React from 'react';
import Select, { Option } from 'react-select';

import { actions } from '../../ducks/MessageForm';
import GlyphPopover from '../../lib/components/GlyphPopover';
import { getDispatch } from '../../lib/dispatchExporter';
import I18n from '../../lib/i18n';
import notify from '../../lib/notify';
import { ICountry } from '../../models/Message';
import Product from '../../models/Product';
import ProductList from '../../models/ProductList';
import ProductDragContainer from './DragComponents/ProductDragContainer';

interface IProps {
  relatedProducts: ProductList;
  countries: List<ICountry>;
  platforms: string[];
  hasNxNewsFormLevel: boolean;
}

interface IState {
  nsUid: string;
}

export default class ProductForm extends React.PureComponent<IProps, IState> {
  constructor(props: IProps) {
    super(props);
    this.state = {
      nsUid: '',
    };
  }
  private dispatch = getDispatch();

  private handleOpenSelect = () => {
    const { relatedProducts } = this.props;
    const hasSuperset = relatedProducts.get('hasSuperset');
    if (!relatedProducts.isFetching && !hasSuperset) {
      this.dispatch(actions.fetchProductsRequest());
    }
  }

  private limitExceeded = () => {
    const { relatedProducts } = this.props;
    const displayType = (window as any).Constants.message.displayTypes.NORMAL;
    const maxProductsLength = (window as any).Constants.validators.message.maxProductsLength[displayType];
    return relatedProducts.productListLimitExceeded(maxProductsLength, I18n.t('activerecord.attributes.message.related_products'));
  };

  private notifyProductNotFound = () => {
    notify({
      title: '',
      message: I18n.t('message_details.error.product_not_found'),
      type: 'danger'
    });
  }

  public selectProduct = (e: Option<string>) => {
    const nsUid = e.value;
    if (nsUid === undefined) { return; }
    if (this.limitExceeded()) {
      return; 
    }
    this.dispatch(actions.selectRelatedProduct(nsUid));
    if (Product.isSoftDetail(nsUid)) {
      this.dispatch(actions.fetchRelatedChannelsRequest({ nsUid }));
    }
  }

  public handleSort = ({ oldIndex, newIndex }: { oldIndex: number, newIndex: number }) => {
    this.dispatch(actions.sortRelatedProducts({ oldIndex, newIndex }));
  }

  public handleUnselect = (id: string) => {
    this.dispatch(actions.unselectRelatedProduct(id));
  }

  public handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ nsUid : e.target.value });
  }

  public handleClick = () => {
    if (this.limitExceeded()) {
      return; 
    }
    const nsUid = this.state.nsUid.trim();
    if (!nsUid) {
      this.notifyProductNotFound();
      return;
    }
    const { relatedProducts, platforms } = this.props;
    // 重複チェック
    if (relatedProducts.selectedIds.includes(nsUid)) {
      this.dispatch(actions.fetchProductByIdFailed(I18n.t('message_details.error.product_duplicated')));
      notify({
        title: '',
        message: I18n.t('message_details.error.product_duplicated'),
        type: 'danger',
      });
      return;
    }
    this.dispatch(actions.fetchRelatedProductByIdRequest({
      nsUid,
      platforms,
      afterFetch: (succeeded: boolean) => {
        if (succeeded) {
          this.setState({nsUid: ''});
        } else {
          this.notifyProductNotFound();
        }
      }
    }));
  }

  public render() {
    const { relatedProducts, countries, platforms, hasNxNewsFormLevel } = this.props;
    const { nsUid } = this.state;
    const parentFormName = 'message[related_products][]';
    return (
      <div>
        <label htmlFor="related-products" className="control-label">
          {I18n.t('activerecord.attributes.message.related_products')}
        </label>
        <GlyphPopover
          title={I18n.t('activerecord.attributes.message.related_products')}
          body={I18n.t('messages.tooltips.products')}
        />
        <br />
        <div className="row">
          { hasNxNewsFormLevel ?
            <>
              <div className="col-md-7">
                <input 
                  type="text"
                  name="related-products-input"
                  placeholder={I18n.t('message_details.form.fetch_product_by_id_placeholder')}
                  value={nsUid}
                  className="form-control"
                  onChange={this.handleChange}
                />
              </div>
              <div className="col-md-5" style={{ marginBottom: '10px' }}>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={this.handleClick}
                  disabled={!nsUid || relatedProducts.isFetching || !countries.size}
                >
                  {I18n.t('common.action.add')}
                </button>
              </div>
            </>
          :
            <div className="col-md-10">
              <Select
                name="related-products-select"
                options={relatedProducts.filterAvailableCountries(countries.map(e => e.iso2).toArray(), platforms)}
                isLoading={relatedProducts.isFetching}
                disabled={relatedProducts.isFetching}
                onChange={this.selectProduct}
                onFocus={this.handleOpenSelect}
                menuContainerStyle={{ position: 'relative' }}
              />
            </div>
          }
          <div style={ hasNxNewsFormLevel ? undefined : { marginTop: '10px' } } className='col-md-12' >
            <ProductDragContainer
              $$products={relatedProducts}
              countries={countries}
              sortProducts={this.handleSort}
              unselectProduct={this.handleUnselect}
              platforms={platforms}
              {...{ parentFormName }}
            />
          </div>
        </div>
      </div>
    );
  }
}
